import { fetcher, SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import { CreateTemplateDTO, UpdateTemplateDTO } from './types';

import { ExemptionCertificateTemplate } from '@prisma/client';

export async function useCreateExemptionPortalTemplate(
  createTemplateDto: CreateTemplateDTO,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: ExemptionCertificateTemplate;
  error?: string;
}> {
  try {
    const response = await fetcher(
      'portal/admin/templates',
      {
        method: 'POST',
        body: createTemplateDto,
      },
      undefined,
      clerkToken
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to create template',
      };
    }

    return {
      success: true,
      data: data as ExemptionCertificateTemplate,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export async function useUpdateExemptionPortalTemplate(
  templateId: string,
  updateTemplateDto: UpdateTemplateDTO,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: ExemptionCertificateTemplate;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/admin/templates/${templateId}`,
      {
        method: 'PUT',
        body: updateTemplateDto,
      },
      undefined,
      clerkToken
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to update template',
      };
    }

    return {
      success: true,
      data: data as ExemptionCertificateTemplate,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

// Common APIs (for both customers and sellers)
export function useExemptionPortalTemplates(
  clerkToken: string,
  active?: boolean
): {
  templates: ExemptionCertificateTemplate[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: ExemptionCertificateTemplate[]
  ) => Promise<ExemptionCertificateTemplate[] | undefined>;
} {
  const queryParams = active !== undefined ? `?active=${active}` : '';

  const { data, error, isLoading, mutate } = useSWR<
    ExemptionCertificateTemplate[]
  >(
    `portal/templates${queryParams}`,
    SWRFetcher({ method: 'GET' }, undefined, clerkToken)
  );

  return {
    templates: data,
    isLoading,
    error,
    mutate,
  };
}

export function useExemptionPortalTemplateById(
  templateId: string,
  clerkToken: string
): {
  template: ExemptionCertificateTemplate | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: ExemptionCertificateTemplate
  ) => Promise<ExemptionCertificateTemplate | undefined>;
} {
  const { data, error, isLoading, mutate } =
    useSWR<ExemptionCertificateTemplate>(
      templateId ? `portal/templates/${templateId}` : null,
      SWRFetcher({ method: 'GET' }, undefined, clerkToken)
    );

  return {
    template: data,
    isLoading,
    error,
    mutate,
  };
}

export function useExemptionPortalTemplatesByExemptionAndJurisdiction(
  exemptionReason: string,
  jurisdiction: string,
  clerkToken: string
): {
  templates: ExemptionCertificateTemplate[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: ExemptionCertificateTemplate[]
  ) => Promise<ExemptionCertificateTemplate[] | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<
    ExemptionCertificateTemplate[]
  >(
    exemptionReason && jurisdiction
      ? `portal/templates/exemption/${exemptionReason}/jurisdiction/${jurisdiction}`
      : null,
    SWRFetcher({ method: 'GET' }, undefined, clerkToken)
  );

  return {
    templates: data,
    isLoading,
    error,
    mutate,
  };
}

export function useExemptionPortalTemplatesByJurisdiction(
  jurisdiction: string,
  clerkToken: string
): {
  templates: ExemptionCertificateTemplate[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: ExemptionCertificateTemplate[]
  ) => Promise<ExemptionCertificateTemplate[] | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<
    ExemptionCertificateTemplate[]
  >(
    jurisdiction ? `portal/templates/jurisdiction/${jurisdiction}` : null,
    SWRFetcher({ method: 'GET' }, undefined, clerkToken)
  );

  return {
    templates: data,
    isLoading,
    error,
    mutate,
  };
}
